import React, { useState, useEffect, useRef } from 'react';
import { FiUser, FiLogOut, FiMenu, FiChevronDown, FiCheckSquare, FiSettings, FiMoreHorizontal } from 'react-icons/fi';
import { IoHome, IoAnalytics, IoScan, IoLocation, IoDocumentText } from "react-icons/io5";
import { FiClock } from "react-icons/fi";
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';
import { logout } from '../../utils/logout';
import api from '../../utils/api';

function Navbar({ onToggleSidebar }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useUser();

  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [showTimesheet, setShowTimesheet] = useState(user && user.role_id != "2");

  const profileRef = useRef();
  const desktopMoreRef = useRef();
  const mobileMoreRef = useRef();

  const isActive = (path) => location.pathname.startsWith(path);

  const IMAGE_BASE_URL = process.env.REACT_APP_IMAGEURL;
  const profileImageUrl = user?.image
    ? (user.image.startsWith("http") ? user.image : `${IMAGE_BASE_URL}/${user.image}`)
    : `${IMAGE_BASE_URL}/uploads/no_image.png`;

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const r = await api.get('/timesheet/user-config');
        if (r.data?.status) setShowTimesheet(!!r.data.requires_approval);
      } catch (e) {
        console.error('Failed to fetch timesheet config');
      }
    };
    if (user) {
      if (user.role_id != "2") setShowTimesheet(true);
      else fetchConfig();
    }
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
      const isOutsideDesktop = desktopMoreRef.current && !desktopMoreRef.current.contains(event.target);
      const isOutsideMobile = mobileMoreRef.current && !mobileMoreRef.current.contains(event.target);
      if (isOutsideDesktop && isOutsideMobile) {
        setShowMoreMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toCamelCase = (str) =>
    str?.toLowerCase().split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');

  // Menu Definitions
  const menuItems = [
    { id: 'home', label: 'HOME', path: '/home', icon: IoHome, primary: true, show: (user) => user?.role_id != 2 },
    { id: 'tickets', label: 'TICKETS', path: '/tickets', icon: FiCheckSquare, primary: true },
    { id: 'timesheet', label: 'TIMESHEET', path: '/timesheet', icon: FiClock, primary: true, isTimesheet: true },
    { id: 'dashboard', label: 'Reports', path: '/dashboard', icon: IoAnalytics, primary: false },
    { id: 'face', label: 'Attendance', path: '/face-attendance', icon: IoScan, primary: false, show: (user) => user?.role_id != 1 && user?.role_id != 2 },
    { id: 'geo', label: 'Tracking', path: '/geofencing', icon: IoLocation, primary: false, show: (user) => user?.role_id == 1 },
    { id: 'documentation', label: 'Documentation', path: '/documentation', icon: IoDocumentText, primary: false, show: (user) => user?.role_id != 2 },
    { id: 'settings', label: 'Settings', path: '/email-settings', icon: FiSettings, primary: false, show: (user) => user?.role_id == 1 },
  ];

  const filteredMenuItems = menuItems.filter(item => {
    if (item.isTimesheet && !showTimesheet) return false;
    if (item.show && !item.show(user)) return false;
    return true;
  });

  const primaryMenus = filteredMenuItems.filter(item => item.primary);
  const moreMenus = filteredMenuItems.filter(item => !item.primary);
  const activeItem = filteredMenuItems.find(item => isActive(item.path));
  const currentModuleName = activeItem ? activeItem.label : 'Menu';

  return (
    <header className="h-15 bg-white shadow-sm flex items-center justify-between px-2 sm:px-4 border-b border-neutral-200 min-w-0 z-50">
      <div className="flex items-center justify-between w-full h-full">
        
        {/* Left Side: Branding */}
        <div className="flex items-center">
          {user?.role_id != 2 && (
            <button onClick={onToggleSidebar} className="text-gray-700 hover:text-main mr-2">
              <FiMenu className="text-2xl" />
            </button>
          )}
          <Link to={user?.role_id == 2 ? "/tickets" : "/home"} className="flex items-center cursor-pointer">
            <img src="/logo.png" alt="Logo" className="w-7 h-7 object-contain" />
            <span className="ml-2 font-heading text-main font-medium hidden sm:block">eNova Administration Portal</span>
            <span className="ml-2 font-heading text-main font-semibold block sm:hidden text-sm">eNova STS</span>
          </Link>
        </div>

        {/* Right Side Group */}
        <div className="flex items-center h-full sm:gap-2">
          
          {/* Desktop Navigation (Icons + Labels) */}
          <div className="hidden md:flex items-center h-full gap-2 mr-4 text-center">
            {primaryMenus.map(item => (
              <Link
                key={item.id}
                to={item.path}
                className={`flex flex-col items-center justify-center p-2 rounded-lg transition-all relative group min-w-[70px]
                  ${isActive(item.path) ? "text-main" : "text-gray-700 hover:text-main hover:bg-gray-100"}
                `}
              >
                <item.icon className="text-xl" />
                <span className="text-[10px] font-bold mt-0.5 tracking-tight uppercase">{item.label}</span>
                <div className="absolute top-[110%] left-1/2 -translate-x-1/2 hidden group-hover:block w-auto whitespace-nowrap text-xs text-white bg-gray-900 rounded-md px-2 py-1 shadow-lg z-[100] pointer-events-none">
                  {item.label}
                </div>
                {isActive(item.path) && <div className="absolute bottom-0 left-1 w-full h-0.5 bg-main rounded-full"></div>}
              </Link>
            ))}

            {moreMenus.length > 0 && (
              <div 
                className="relative h-full flex items-center" 
                ref={desktopMoreRef}
                onMouseEnter={() => setShowMoreMenu(true)}
                onMouseLeave={() => setShowMoreMenu(false)}
              >
                <button
                  onClick={() => setShowMoreMenu(!showMoreMenu)}
                  className={`flex flex-col items-center justify-center p-2 rounded-lg transition-all group min-w-[60px]
                    ${showMoreMenu || moreMenus.some(m => isActive(m.path)) ? "text-main bg-gray-50" : "text-gray-700 hover:text-main hover:bg-gray-100"}
                  `}
                >
                  <FiMoreHorizontal className="text-xl" />
                  <span className="text-[10px] font-bold mt-0.5 tracking-tight uppercase flex items-center">
                    MORE <FiChevronDown className={`ml-0.5 transition-transform ${showMoreMenu ? 'rotate-180' : ''}`} />
                  </span>
                </button>
                {showMoreMenu && (
                  <div className="absolute right-0 translate-x-6 top-full pt-1 z-[100]">
                    <div className="w-48 bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150">
                      <div className="py-1">
                      {moreMenus.map(item => (
                        <Link
                          key={item.id}
                          to={item.path}
                          onClick={(e) => { e.stopPropagation(); setShowMoreMenu(false); }}
                          className={`w-full flex items-center px-4 py-3 text-sm transition-colors text-left
                            ${isActive(item.path) ? "bg-main text-white" : "text-gray-700 hover:bg-gray-100 hover:text-main"}
                          `}
                        >
                          <item.icon className="text-lg mr-3" />
                          <span className="font-medium">{item.label}</span>
                        </Link>
                      ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Mobile Navigation */}
          <div className="flex md:hidden items-center flex-1 justify-center px-2">
             <div className="flex items-center bg-gray-100 rounded-full px-4 py-1.5 shadow-inner">
               <span className="text-main font-bold text-xs uppercase truncate max-w-[100px]">{currentModuleName}</span>
               <div className="w-px h-4 bg-gray-300 mx-3"></div>
               <div className="relative" ref={mobileMoreRef}>
                 <button onClick={() => setShowMoreMenu(!showMoreMenu)} className="p-1 hover:bg-gray-200 rounded-full">
                   <FiMoreHorizontal className="text-xl text-gray-700" />
                 </button>
                 {showMoreMenu && (
                   <div className="fixed top-14 left-4 right-4 bg-white border rounded-2xl shadow-2xl p-4 grid grid-cols-2 gap-3 z-[100] animate-in zoom-in-95">
                     {filteredMenuItems.map(item => (
                       <Link key={item.id} to={item.path} onClick={(e) => { e.stopPropagation(); setShowMoreMenu(false); }} className={`flex flex-col items-center p-4 rounded-xl ${isActive(item.path) ? 'bg-main text-white' : 'bg-gray-50 text-gray-700'}`}>
                         <item.icon className="text-2xl mb-1" />
                         <span className="text-xs font-bold">{item.label}</span>
                       </Link>
                     ))}
                   </div>
                 )}
               </div>
             </div>
          </div>

          {/* User Profile Menu (Restored to User Style) */}
          <div className="flex items-center" ref={profileRef}>
            <button
              onClick={() => setShowProfileMenu(!showProfileMenu)}
              className="flex items-center gap-2 px-2 sm:px-3 py-1 bg-gray-100 rounded-full text-sm text-gray-800 hover:bg-main hover:text-white transition group"
            >
              <div className="w-8 h-8 rounded-full overflow-hidden border border-gray-200 bg-gray-200">
                {user?.image ? (
                  <img src={profileImageUrl} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  <FiUser className="text-gray-500 w-full h-full p-1.5 group-hover:text-white" />
                )}
              </div>
              <span className="hidden sm:inline font-medium truncate max-w-[8rem]">
                {toCamelCase(user?.first_name)} {toCamelCase(user?.last_name)}
              </span>
              <FiChevronDown className={`text-base transition-transform ${showProfileMenu ? 'rotate-180' : ''}`} />
            </button>

            {showProfileMenu && (
              <div className="absolute right-4 mt-12 top-0 bg-white border border-gray-100 rounded-lg shadow-xl z-[100] w-44 flex flex-col text-sm overflow-hidden animate-in slide-in-from-top-2 duration-150">
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
                   <p className="text-xs font-bold truncate">{user?.first_name} {user?.last_name}</p>
                </div>
                <Link
                  to="/profile"
                  onClick={() => setShowProfileMenu(false)}
                  className="flex items-center px-4 py-3 hover:bg-gray-100 text-left border-b border-gray-50 text-gray-700"
                >
                  <FiUser className="mr-2" /> Profile
                </Link>
                <button
                  onClick={() => { setShowProfileMenu(false); logout(); }}
                  className="flex items-center px-4 py-3 hover:bg-red-50 hover:text-red-600 text-left text-gray-700"
                >
                  <FiLogOut className="mr-2" /> Logout
                </button>
              </div>
            )}
          </div>

        </div>
      </div>
    </header>
  );
}

export default Navbar;
